/**
 * Quality / Compliance mapping (contract v3, GET /quality/compliance): the controls of OWASP ASVS 4.0.3 and ISO/IEC
 * 27001:2022 Annex A, one framework at a time, filtered by status. Each control: its status (evidenced, failing, no
 * evidence yet, not covered by Flow), "partly" where Flow can only partly evidence it, the sources that help with it
 * and the evidence (a run opens its check, an attestation the release in Build provenance). The disclaimer always
 * shows. The wording is "evidence for" and "helps with", never "compliant" or "certified".
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DownloadSimple, Printer } from '@phosphor-icons/react';
import { complianceReport, getCompliance } from '../../services/QualityService';
import { Pill, State, SubHead } from './QualityCommon';
import { FrameworkTotals, openPrintableReport } from './QualityProveCards';
import {
  CONTROL_STATUS, PROVE_AREAS, SOURCE_STATE, controlStatusOf, errorText, relativeTime,
} from './qualityModel';

const FILTERS = [{ id: null, label: 'All' }, ...Object.entries(CONTROL_STATUS).map(([id, s]) => ({ id, label: s.label }))];

function Evidence({ item, onOpenCheck, onOpenProvenance }) {
  const state = SOURCE_STATE[item.state] || SOURCE_STATE.none;
  const when = item.at ? ` · ${relativeTime(item.at)}` : '';
  let body = <span>{item.label}</span>;
  if (item.kind === 'run' && item.check) {
    body = <button className="quality-link" onClick={() => onOpenCheck(item.check)} type="button">{item.label}</button>;
  } else if (item.kind === 'attestation') {
    body = <button className="quality-link" onClick={() => onOpenProvenance(item.id)} type="button">{item.label}</button>;
  }
  return (
    <li>
      <Pill className="is-xs" tone={state.tone}>{state.label}</Pill>
      {body}
      <span className="quality-muted">{when}</span>
    </li>
  );
}

Evidence.propTypes = { item: PropTypes.object.isRequired, onOpenCheck: PropTypes.func.isRequired, onOpenProvenance: PropTypes.func.isRequired };

function Control({ control, onOpenCheck, onOpenProvenance }) {
  const status = controlStatusOf(control.status);
  return (
    <li className={`quality-comp-control is-${control.status}`} data-testid={`quality-control-${control.id}`}>
      <div className="quality-prov-line">
        <code className="quality-mono quality-comp-id">{control.id}</code>
        <strong className="quality-grow">{control.title}</strong>
        {control.partial && <Pill className="is-xs" tone="info" title="Flow can only partly give evidence for this control">partly</Pill>}
        <Pill className="is-xs" tone={status.tone}>{status.label}</Pill>
      </div>
      {control.group && <span className="quality-muted quality-small">{control.group}</span>}
      {control.note && <span className="quality-small">{control.note}</span>}
      {(control.sources || []).length > 0 && (
        <div className="quality-small quality-comp-sources">
          <span className="quality-muted">Helps with it: </span>
          {control.sources.map((s, i) => {
            const st = SOURCE_STATE[s.state] || SOURCE_STATE.none;
            return <span className={`quality-comp-source is-${st.tone}`} key={`${s.type}-${s.id || i}`} title={st.label}>{s.label} ({st.label.toLowerCase()})</span>;
          })}
        </div>
      )}
      {(control.evidence || []).length > 0 && (
        <ul aria-label={`Evidence for ${control.id}`} className="quality-comp-evidence quality-small">
          {control.evidence.map((item) => <Evidence item={item} key={`${item.kind}-${item.id}`} onOpenCheck={onOpenCheck} onOpenProvenance={onOpenProvenance} />)}
        </ul>
      )}
      {control.status === 'not_covered' && <span className="quality-muted quality-small">Flow has nothing for this control: it is up to your team and processes.</span>}
    </li>
  );
}

Control.propTypes = { control: PropTypes.object.isRequired, onOpenCheck: PropTypes.func.isRequired, onOpenProvenance: PropTypes.func.isRequired };

export default function QualityCompliance({ projectId, onBack, onOpenCheck, onOpenProvenance }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [frameworkId, setFrameworkId] = useState(null);
  const [filter, setFilter] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try { setAnswer(await getCompliance(projectId)); } catch (e) { setError(e); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const frameworks = answer?.frameworks || [];
  const framework = frameworks.find((f) => f.id === frameworkId) || frameworks[0] || null;
  const controls = (framework?.controls || []).filter((c) => !filter || c.status === filter);
  const countOf = (id) => (framework?.controls || []).filter((c) => !id || c.status === id).length;

  const json = async () => {
    setNotice(null);
    try { await complianceReport(projectId, 'json'); } catch (e) { setNotice(`Could not download the report: ${errorText(e)}`); }
  };
  const print = async () => {
    setNotice(null);
    try { await openPrintableReport(projectId); } catch (e) { setNotice(`Could not open the printable report: ${errorText(e)}`); }
  };

  return (
    <div className="quality-sub" data-testid="quality-compliance">
      <SubHead onBack={onBack} title={PROVE_AREAS.compliance.title}>
        <span className="quality-grow" />
        <button className="quality-btn" onClick={json} type="button"><DownloadSimple aria-hidden size={14} />Download report (JSON)</button>
        <button className="quality-btn" onClick={print} type="button"><Printer aria-hidden size={14} />Open printable report</button>
      </SubHead>
      <p className="quality-muted">{PROVE_AREAS.compliance.purpose}</p>
      <p className="quality-banner is-info" data-testid="quality-comp-disclaimer" role="note">
        {answer?.disclaimer || 'This shows the evidence Flow has for some controls. It helps with an assessment; it is not an audit.'}
      </p>
      {notice && <p className="quality-banner is-warn" role="alert">{notice}</p>}
      {error && !answer && <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the compliance mapping" tone="bad">{errorText(error)}</State>}
      {!error && !answer && <div className="quality-state" role="status">Loading the controls…</div>}

      {framework && (
        <>
          <div aria-label="Framework" className="quality-chips" role="group">
            <span className="quality-cap">Framework</span>
            {frameworks.map((f) => (
              <button aria-pressed={f.id === framework.id} className="quality-chip" key={f.id} onClick={() => { setFrameworkId(f.id); setFilter(null); }} type="button">
                {f.name}{f.version && !String(f.name).includes(f.version) ? ` ${f.version}` : ''}
              </button>
            ))}
          </div>
          <section aria-labelledby="quality-comp-title" className="quality-card quality-pad">
            <h2 id="quality-comp-title">Evidence for {framework.name}{framework.version && !String(framework.name).includes(framework.version) ? ` ${framework.version}` : ''}</h2>
            <div className="quality-comp-summary"><FrameworkTotals totals={framework.totals} /></div>
            {framework.totals?.partial > 0 && <p className="quality-muted quality-small">{framework.totals.partial} of the controls Flow helps with are covered only partly.</p>}
            <div aria-label="Status" className="quality-chips" role="group">
              <span className="quality-cap">Status</span>
              {FILTERS.map((f) => (
                <button aria-pressed={filter === f.id} className="quality-chip" key={f.id || 'all'} onClick={() => setFilter(f.id)} type="button">{f.label} ({countOf(f.id)})</button>
              ))}
            </div>
            {controls.length ? (
              <ul aria-label="Controls" className="quality-comp-controls">
                {controls.map((control) => <Control control={control} key={control.id} onOpenCheck={onOpenCheck} onOpenProvenance={onOpenProvenance} />)}
              </ul>
            ) : <p className="quality-muted">No control with this status.</p>}
          </section>
        </>
      )}
    </div>
  );
}

QualityCompliance.propTypes = {
  onBack: PropTypes.func.isRequired,
  // (checkId) => void: a run's evidence opens its check.
  onOpenCheck: PropTypes.func.isRequired,
  // (provenanceId) => void: an attestation opens that release in Build provenance.
  onOpenProvenance: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};
